import './navbar.scss';

import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { AlmaLink } from '../alma-link';
import * as styles from './navbar.module.css';

export function Navbar({ navContent = [], navTransparency }) {
	const [scrolling, setScrolling] = useState(false);
	const [navHidden, setNavHidden] = useState(false);
	const [pageLoaded, setPageLoaded] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const nestedMenus = useRef();

	useEffect(() => {
		nestedMenus.current = document?.querySelectorAll('.nested-menu');
		let initialScrollPos = 0;
		let growSurfBanner;

		const setScrollHandler = () => {
			growSurfBanner = document.querySelector(
				'#grsf-referred-experience'
			);

			if (window.pageYOffset === 0) {
				setScrolling(false);

				nestedMenus.current.forEach((nestedMenu) => {
					nestedMenu.classList.contains('dropdownMenuOpen') &&
						nestedMenu.classList.remove('dropdownMenuOpen');
				});

				handleShowNav(growSurfBanner);
			} else {
				window.pageYOffset < 300 && setScrolling(true);

				if (window.pageYOffset < initialScrollPos) {
					setScrolling(true);
					handleShowNav(growSurfBanner);
					initialScrollPos = window.pageYOffset;
				} else if (
					window.pageYOffset >
					initialScrollPos + window.innerHeight
				) {
					nestedMenus.current.forEach((nestedMenu) => {
						nestedMenu.classList.contains('dropdownMenuOpen') &&
							nestedMenu.classList.remove('dropdownMenuOpen');
					});

					initialScrollPos = window.pageYOffset;
					if (pageLoaded) {
						handleHideNav(growSurfBanner);
					}
				}
			}
		};

		if (!pageLoaded) {
			setTimeout(() => {
				setPageLoaded(true);
			}, 500);
			if (window.pageYOffset > 0) {
				handleShowNav(growSurfBanner);
				setScrolling(true);
			}
		}

		if (typeof window !== 'undefined') {
			window.addEventListener('scroll', setScrollHandler);
		}

		return () => {
			window.removeEventListener('scroll', setScrollHandler);
		};
	}, [mobileMenuOpen, pageLoaded]);

	const handleShowNav = (growSurfBanner) => {
		setNavHidden(false);
		growSurfBanner?.classList.remove('nav-closed');
	};

	const handleHideNav = (growSurfBanner) => {
		setNavHidden(true);
		growSurfBanner?.classList.add('nav-closed');
	};

	const handleOpenDropdown = (event) => {
		const dropdownMenus = document.querySelectorAll('.nested-menu');
		dropdownMenus.forEach((menu) => {
			if (
				menu !== event.currentTarget &&
				menu.classList.contains('dropdownMenuOpen')
			) {
				menu.classList.remove('dropdownMenuOpen');
			}
		});

		if (event.currentTarget.classList.contains('dropdownMenuOpen')) {
			event.currentTarget.classList.remove('dropdownMenuOpen');
		} else {
			event.currentTarget.classList.add('dropdownMenuOpen');
			setScrolling(true);
		}
	};

	const handleKeyboardOpenDropdown = (event) => {
		if (event.key === 'Enter') {
			handleOpenDropdown(event);
		}
	};

	// close dropdown menu when clicking outside of it
	useEffect(() => {
		nestedMenus.current = document?.querySelectorAll('.nested-menu');

		const closeDropdowns = (e) => {
			if (e.target.closest('.nested-menu') === null) {
				nestedMenus.current.forEach((nestedMenu) => {
					nestedMenu.classList.contains('dropdownMenuOpen') &&
						nestedMenu.classList.remove('dropdownMenuOpen');
				});
			}
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('click', closeDropdowns);
		}
		return () => {
			window.removeEventListener('click', closeDropdowns);
		};
	}, []);

	// nav data
	const simpleMenu = navContent?.navMenu?.simpleMenu;
	const transparentLightText = navTransparency === 'transparentLightText';
	const transparentDarkText = navTransparency === 'transparentDarkText';

	return (
		<div
			className={classNames(
				'scrollNav',
				styles.moduleContainer,
				scrolling ? 'scrolled' : 'scroll-top',
				navHidden && 'hide',
				!mobileMenuOpen && navTransparency
			)}
		>
			<nav className={styles.navContainer} role='menubar'>
				{!simpleMenu && (
					<div
						className={classNames(
							mobileMenuOpen && styles.change,
							styles.hamburger
						)}
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						role='button'
						aria-label='menu'
						tabIndex={0}
					>
						<div
							className={classNames(
								scrolling && styles.scrolledBar,
								styles.bar1,
								transparentLightText && styles.lightBar
							)}
						/>
						<div
							className={classNames(
								scrolling && styles.scrolledBar,
								styles.bar2,
								transparentLightText && styles.lightBar
							)}
						/>
						<div
							className={classNames(
								scrolling && styles.scrolledBar,
								styles.bar3,
								transparentLightText && styles.lightBar
							)}
						/>
					</div>
				)}
				<a title='Home Link' href='https://helloalma.com/'>
					{transparentLightText && !scrolling && !mobileMenuOpen ? (
						<img src='/logo-white.inline.svg' alt='Alma Logo' />
					) : (
						<img src='/logo-green.inline.svg' alt='Alma Logo' />
					)}
				</a>
				{simpleMenu ? (
					<ul
						className={classNames(
							mobileMenuOpen && styles.mobileOpen
						)}
						role='menubar'
					>
						{navContent?.items?.map((menuItem) => {
							const { _key, _type, hideOnMobile, title } =
								menuItem;

							if (_type === 'cta') {
								const analytics =
									menuItem._rawAnalytics || null;

								return (
									<li
										className={classNames(
											hideOnMobile && styles.hideOnMobile
										)}
										key={_key}
										role='menuitem'
									>
										<AlmaLink
											analytics={analytics}
											cta={menuItem}
										/>
									</li>
								);
							} else if (_type === 'nestedMenu') {
								return (
									<li
										className={classNames(
											hideOnMobile && styles.hideOnMobile
										)}
										key={_key}
										role='menuitem'
									>
										<div
											className={classNames(
												'nested-menu',
												styles.dropdownMenuContainer
											)}
											onClick={(event) =>
												handleOpenDropdown(event)
											}
											onKeyDown={(event) =>
												handleKeyboardOpenDropdown(
													event
												)
											}
											tabIndex={0}
											role='menu'
										>
											<div
												className={
													styles.dropdownMenuItemContainer
												}
											>
												<p>{title}</p>
												<img
													src='/menu-arrow.inline.svg'
													className={classNames(
														styles.menuArrow
													)}
												/>
											</div>
											<ul
												className={styles.dropdownMenu}
												role='menu'
											>
												{menuItem.nestedMenuCtas.map(
													(nestedMenuItem) => {
														const { _key } =
															nestedMenuItem;
														const analytics =
															nestedMenuItem._rawAnalytics ||
															null;

														return (
															<li
																className={
																	styles.dropdownMenuItem
																}
																key={_key}
																role='menuitem'
															>
																<AlmaLink
																	analytics={
																		analytics
																	}
																	cta={
																		nestedMenuItem
																	}
																/>
															</li>
														);
													}
												)}
											</ul>
										</div>
									</li>
								);
							}
						})}
					</ul>
				) : (
					<>
						<ul
							className={classNames(
								mobileMenuOpen && styles.mobileOpen,
								styles.linksContainer
							)}
							role='menubar'
						>
							{navContent?.items?.map((menuItem) => {
								const { _key, _type, hideOnMobile, title } =
									menuItem;

								if (_type === 'cta') {
									const analytics =
										menuItem._rawAnalytics || null;

									return (
										<li
											className={classNames(
												hideOnMobile &&
													styles.hideOnMobile,
												styles.mainItem
											)}
											key={_key}
											role='menuitem'
										>
											<AlmaLink
												analytics={analytics}
												cta={menuItem}
											/>
										</li>
									);
								} else if (_type === 'nestedMenu') {
									return (
										<li
											className={classNames(
												styles.mainItem,
												hideOnMobile &&
													styles.hideOnMobile
											)}
											key={_key}
											role='menuitem'
										>
											<div
												className={classNames(
													'nested-menu',
													styles.dropdownMenuContainer
												)}
												onClick={(event) =>
													handleOpenDropdown(event)
												}
												onKeyDown={(event) =>
													handleKeyboardOpenDropdown(
														event
													)
												}
												tabIndex={0}
												role='menu'
											>
												<div
													className={
														styles.dropdownMenuItemContainer
													}
												>
													<p>{title}</p>
													<img
														src='/menu-arrow.inline.svg'
														className={classNames(
															styles.menuArrow
														)}
													/>
												</div>
												<ul
													className={
														styles.dropdownMenu
													}
													role='menu'
												>
													{menuItem.nestedMenuCtas.map(
														(nestedMenuItem) => {
															const { _key } =
																nestedMenuItem;
															const analytics =
																nestedMenuItem._rawAnalytics ||
																null;

															return (
																<li
																	className={
																		styles.dropdownMenuItem
																	}
																	key={_key}
																	role='menuitem'
																>
																	<AlmaLink
																		analytics={
																			analytics
																		}
																		cta={
																			nestedMenuItem
																		}
																	/>
																</li>
															);
														}
													)}
												</ul>
											</div>
										</li>
									);
								}
							})}
						</ul>
						<a
							className={styles.profileIcon}
							href='https://secure.helloalma.com/login/'
							title='login'
						>
							{mobileMenuOpen ? (
								<img src='/profile-icon.svg' />
							) : scrolling ||
							  !navTransparency ||
							  transparentDarkText ? (
								<img src='/profile-icon.svg' />
							) : (
								<img src='/profile-icon-white.svg' />
							)}
						</a>
					</>
				)}
			</nav>
		</div>
	);
}
